// src/pages/AdminAchievements.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../services/authService';
import LoadingState from '../components/common/LoadingState';
import { ArrowLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API_CONFIG } from '../components/config/api';

interface Category {
  id: number;
  name: string;
  display_name: string;
  achievement_count: number;
}

interface Achievement {
  id: number;
  record_hash: string;
  name: string;
  description: string;
  icon_url: string | null;
  is_secret: boolean;
  points: number;
  category_id: number | null;
  category_name: string | null;
}

const AdminAchievements: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [filteredAchievements, setFilteredAchievements] = useState<Achievement[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const pageSize = 25;

  useEffect(() => {
    // Check if user is authenticated and has admin rights
    const checkAdminAccess = async () => {
      if (!isAuthenticated()) {
        navigate('/login', { state: { returnPath: '/admin/achievements' } });
        return;
      }

      // For now, we assume the user is an admin if they're authenticated
      // In a real application, you would check their role
      setIsAdmin(true);

      try {
        setLoading(true);
        // Fetch categories
        const categoriesResponse = await axios.get(
          `${API_CONFIG.BASE_URL}/admin/achievements?action=categories`, 
          { headers: { Authorization: `Bearer ${localStorage.getItem('destiny_auth_token')}` } }
        );
        
        if (categoriesResponse.data && categoriesResponse.data.status === 'success') {
          setCategories(categoriesResponse.data.data);
        }

        // Fetch achievements (first page)
        await fetchAchievements(1, 'all');
      } catch (err: any) {
        console.error('Error loading admin achievements:', err);
        setError(err.message || 'Failed to load achievements');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAccess();
  }, [navigate]);

  const fetchAchievements = async (pageNum: number, category: string, search: string = '') => {
    try {
      setLoading(true);
      
      let endpoint = `${API_CONFIG.BASE_URL}/admin/achievements?action=list&page=${pageNum}&limit=${pageSize}`;
      
      if (category !== 'all') {
        endpoint += `&category=${category}`;
      }
      
      if (search) {
        endpoint += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${localStorage.getItem('destiny_auth_token')}` }
      });
      
      if (response.data && response.data.status === 'success') {
        setAchievements(response.data.data.achievements);
        setFilteredAchievements(response.data.data.achievements);
        setTotalPages(Math.ceil(response.data.data.total / pageSize));
      } else {
        throw new Error('Failed to fetch achievements');
      }
    } catch (err: any) {
      console.error('Error fetching achievements:', err);
      setError(err.message || 'Failed to fetch achievements');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setPage(1);
    fetchAchievements(1, category, searchTerm);
  };

  const handleSearch = () => {
    setPage(1);
    fetchAchievements(1, selectedCategory, searchTerm);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      fetchAchievements(newPage, selectedCategory, searchTerm);
    }
  };

  if (!isAdmin && !loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-red mb-4">Access Denied</h2>
        <p className="text-gray-300 mb-4">
          You do not have administrator rights to access this page.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-destiny-gold">Manage Achievements</h1>
        <Link 
          to="/admin" 
          className="inline-flex items-center text-destiny-blue hover:text-destiny-gold"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back to Admin
        </Link>
      </div>
      
      {error && (
        <div className="bg-destiny-red bg-opacity-20 border border-destiny-red rounded-lg p-4 mb-6">
          <h3 className="text-destiny-red font-medium mb-2">Error</h3>
          <p className="text-gray-300">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="mt-2 text-white bg-destiny-red bg-opacity-30 hover:bg-opacity-50 px-3 py-1 rounded text-sm"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Filters and Search */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          {/* Category Filter */}
          <div className="flex-1">
            <label htmlFor="category" className="block text-sm font-medium text-gray-400 mb-1">
              Category
            </label>
            <select
              id="category"
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full bg-gray-700 text-white rounded py-2 px-3 focus:outline-none focus:ring-2 focus:ring-destiny-gold"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.display_name} ({category.achievement_count})
                </option>
              ))}
            </select>
          </div>
          
          {/* Search */}
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-400 mb-1">
              Search
            </label>
            <div className="relative">
              <input
                id="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search by name or description"
                className="w-full bg-gray-700 text-white rounded py-2 pl-3 pr-10 focus:outline-none focus:ring-2 focus:ring-destiny-gold"
              />
              <button
                onClick={handleSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <LoadingState message="Loading achievements..." />
      )}
      
      {/* Achievements Table */}
      {!loading && (
        <>
          <div className="bg-gray-800 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Icon
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Points
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Secret
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {filteredAchievements.length > 0 ? (
                    filteredAchievements.map((achievement) => (
                      <tr key={achievement.id} className="hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {achievement.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {achievement.icon_url ? (
                            <img 
                              src={achievement.icon_url} 
                              alt={achievement.name} 
                              className="h-8 w-8 rounded"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = '/placeholder-emblem.png';
                              }}
                            />
                          ) : (
                            <div className="h-8 w-8 bg-gray-600 rounded"></div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                          {achievement.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {achievement.category_name || 'Uncategorized'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {achievement.points}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {achievement.is_secret ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-destiny-purple text-white">
                              Secret
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-600 text-gray-300">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <Link
                            to={`/admin/achievements/${achievement.id}`}
                            className="text-destiny-blue hover:text-destiny-gold mr-3"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => {
                              /* Implementation for view details */
                              console.log('View details for', achievement.id);
                            }}
                            className="text-gray-400 hover:text-white"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-400">
                        No achievements found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-800 rounded-lg">
            <div className="flex-1 flex justify-between">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-gray-200 bg-gray-700 ${
                  page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600'
                }`}
              >
                Previous
              </button>
              <div className="hidden md:flex">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                   let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 mx-1 border ${
                        page === pageNum
                          ? 'border-destiny-gold bg-gray-700 text-destiny-gold'
                          : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
                      } text-sm font-medium rounded-md`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <div className="flex md:hidden">
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-700 bg-gray-800 text-sm font-medium rounded-md text-gray-300">
                  Page {page} of {totalPages}
                </span>
              </div>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-gray-200 bg-gray-700 ${
                  page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAchievements;